// TYPES FOR FILTERING : "string","number","select","boolean", "none"
import type { ValueFormatterParams } from 'ag-grid-community';
import { ProgressBar } from 'react-bootstrap';
import BooleanRenderer from '../common/components/CellRenderers/BooleanRenderer';
import URLRenderer from '../common/components/CellRenderers/URLRenderer';
import DrilldownToReferralPageCell from './components/DrilldownToReferralPageCell';
import BacklinkUrlCell from './components/BacklinkUrlCell';
import BacklinkAnchorTextCell from './components/BacklinkAnchorTextCell';
import ReferralCountryCell from './components/ReferralCountryCell';
import SubtleBadge from '@/components/common/SubtleBadge';
import { getColor } from '@/helpers/utils';
import {
  isRedirectedUrl,
  linkUrlBadges,
  referringPageBadges,
  MOZ_GROUP_TOOLTIP,
  MOZ_DA_TOOLTIP,
  MOZ_SS_TOOLTIP,
  AHREFS_DR_TOOLTIP,
  LINK_GROUP_TOOLTIP,
  LINK_HREF_TOOLTIP,
  LINK_ANCHOR_TOOLTIP,
  REL_GROUP_TOOLTIP,
  REL_DOFOLLOW_TOOLTIP,
  REL_EXTERNAL_TOOLTIP,
  REL_UGC_TOOLTIP,
  REL_ALTERNATE_TOOLTIP,
  REL_NOOPENER_TOOLTIP,
  REL_NOREFERRER_TOOLTIP,
  MOZ_SPAM_SCORE_TOOLTIP,
  BACKLINK_SPAM_SCORE_TOOLTIP
} from './utils/backlinkCellFlags';
import { referringIpDrilldownPrefix } from './utils/referringIpSubnet';

const getSpamDomainLabel = score => {
  const numericScore = Number(score);
  if (numericScore >= 61) {
    return { label: 'دامنه اسپم: پرخطر', color: 'danger' };
  }
  if (numericScore >= 31) {
    return { label: 'دامنه اسپم: کم‌خطر', color: 'warning' };
  }
  if (numericScore >= 25) {
    return { label: 'نزدیک به منطقه کم‌خطر', color: 'secondary' };
  }
  return null;
};

const safeDecode = (value?: string | null): string => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// REFERRAL DOMAINS
export const ReferralDomains = [
  {
    searchable: true,
    field: 'host_name_from',
    type: 'string',
    title: 'نشانی دامنه',
    flex: 2,
    minWidth: 200,
    cellRenderer: params => {
      const domain = String(params.value ?? '').trim();
      if (!domain) return null;
      const homepage = /^https?:\/\//i.test(domain)
        ? domain
        : `https://${domain}`;

      return (
        <a
          href={homepage}
          target="_blank"
          rel="noreferrer noopener"
          className="referral-domain-cell-link"
          dir="ltr"
          title={`بازکردن ${homepage}`}
        >
          <img
            loading="lazy"
            width="16"
            height="16"
            className="rounded-circle"
            src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(domain)}`}
            alt=""
            onError={event => {
              event.currentTarget.style.display = 'none';
            }}
          />
          <span>{domain}</span>
        </a>
      );
    }
  },
  {
    searchable: false,
    field: 'backlink_count',
    type: 'number',
    title: 'تعداد بک‌لینک',
    flex: 1,
    minWidth: 100,
    cellRenderer: params => (
      <DrilldownToReferralPageCell
        displayValue={params.value}
        filterKey="HostNameFrom"
        filterMode="contains"
        filterValue={params.data?.host_name_from}
      />
    )
  },
  {
    searchable: false,
    field: 'moz_domain_authority',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'DA',
    tooltip: MOZ_DA_TOOLTIP,
    width: 67,
    minWidth: 67,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'domain_from_rating_ahrefs',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'DR',
    tooltip: AHREFS_DR_TOOLTIP,
    width: 67,
    minWidth: 67,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'moz_spam_score',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'SS',
    tooltip: `${MOZ_SS_TOOLTIP}\n${MOZ_SPAM_SCORE_TOOLTIP}`,
    width: 74,
    minWidth: 74,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'domain_from_country',
    type: 'string',
    title: 'مکان دامنه',
    flex: 1,
    minWidth: 100,
    cellRenderer: params => (
      <ReferralCountryCell
        country={params.value}
        countryCode={params.data?.domain_from_country_code}
      />
    )
  },
  {
    searchable: false,
    field: 'tld_from',
    type: 'string',
    title: 'پسوند دامنه',
    flex: 1,
    minWidth: 80
  },
  {
    searchable: false,
    field: 'page_from_language',
    type: 'string',
    title: 'زبان دامنه',
    flex: 1,
    minWidth: 80
  },
  {
    searchable: false,
    noFilter: true,
    field: 'link_last_seen',
    type: 'date',
    title: 'آخرین بررسی',
    flex: 1,
    minWidth: 100
  }
];

// REFERRAL PAGES
export const ReferralPagesColumns = [
  {
    searchable: true,
    field: 'url_from',
    type: 'string',
    wrapText: true,
    autoHeight: true,
    minWidth: 260,
    flex: 1,
    gp: 'صفحه ارجاع (مکان لینک)',
    title: 'نشانی + جزئیات',
    cellStyle: {
      whiteSpace: 'normal',
      lineHeight: 1.35
    },
    cellRenderer: params => {
      const data = params.data;
      return (
        <BacklinkUrlCell
          url={params.value}
          boldDomain
          hostName={data?.host_name_from}
          breakAll
          isRedirected={isRedirectedUrl(
            data?.url_from_redirect_target,
            data?.url_from_is_indirect_link
          )}
          redirectTarget={data?.url_from_redirect_target}
          redirectStatusCode={data?.url_from_redirect_status_code}
          badges={referringPageBadges(data)}
        />
      );
    }
  },
  {
    searchable: true,
    field: 'page_from_title',
    type: 'string',
    gp: 'صفحه ارجاع (مکان لینک)',
    title: 'عنوان صفحه',
    cellStyle: {
      whiteSpace: 'normal',
      overflow: 'hidden',
      paddingTop: '5px',
      textAlign: 'center'
    },
    cellRenderer: params => (
      <div className="backlink-page-title-cell" title={params.value || ''}>
        {params.value || ''}
      </div>
    )
  },
  {
    searchable: false,
    field: 'moz_domain_authority',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'DA',
    tooltip: MOZ_DA_TOOLTIP,
    width: 67,
    minWidth: 67,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'domain_from_rating_ahrefs',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'DR',
    tooltip: AHREFS_DR_TOOLTIP,
    width: 67,
    minWidth: 67,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'moz_spam_score',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'SS',
    tooltip: `${MOZ_SS_TOOLTIP}\n${MOZ_SPAM_SCORE_TOOLTIP}`,
    width: 74,
    minWidth: 74,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'backlink_spam_score',
    type: 'number',
    gp: 'صفحه ارجاع (مکان لینک)',
    title: 'لینک\nاسپم',
    tooltip: BACKLINK_SPAM_SCORE_TOOLTIP,
    width: 100,
    minWidth: 100,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'page_from_internal_links',
    type: 'number',
    gp: 'صفحه ارجاع (مکان لینک)',
    title: 'لینک\nداخلی',
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'page_from_external_links',
    type: 'number',
    gp: 'صفحه ارجاع (مکان لینک)',
    title: 'لینک\nخارجی',
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true
  },
  {
    searchable: true,
    field: 'url_to',
    type: 'string',
    autoHeight: true,
    minWidth: 260,
    gp: 'پیوند (لینک ساخته شده)',
    gptooltip: LINK_GROUP_TOOLTIP,
    title: 'نشانی (href)',
    tooltip: LINK_HREF_TOOLTIP,
    cellRenderer: params => {
      const data = params.data;
      return (
        <BacklinkUrlCell
          url={params.value}
          isRedirected={isRedirectedUrl(
            data?.redirect_target,
            data?.is_indirect_link
          )}
          redirectTarget={data?.redirect_target}
          redirectStatusCode={data?.redirect_status_code}
          indirectLinkPath={data?.indirect_link_path}
          badges={linkUrlBadges(data)}
        />
      );
    }
  },
  {
    searchable: true,
    field: 'anchor',
    type: 'string',
    wrapText: true,
    autoHeight: true,
    width: 260,
    minWidth: 260,
    gp: 'پیوند (لینک ساخته شده)',
    gptooltip: LINK_GROUP_TOOLTIP,
    title: 'متن (anchor)',
    tooltip: LINK_ANCHOR_TOOLTIP,
    cellStyle: {
      whiteSpace: 'normal',
      overflow: 'hidden',
      paddingTop: '5px',
      textAlign: 'center'
    },
    cellRenderer: params => {
      return (
        <BacklinkAnchorTextCell
          anchor={params.data?.anchor}
          textPre={params.data?.text_pre}
          textPost={params.data?.text_post}
          url={params.data?.url_to}
        />
      );
    }
  },

  {
    searchable: false,
    field: 'dofollow',
    type: 'boolean',
    gp: 'اتریبیوت پیوند (rel)',
    gptooltip: REL_GROUP_TOOLTIP,
    title: 'dofollow',
    tooltip: REL_DOFOLLOW_TOOLTIP,
    width: 90,
    minWidth: 90,
    suppressHeaderMenuButton: true,
    cellRenderer: params => <BooleanRenderer boolValue={params.value} />
  },

  {
    searchable: false,
    field: 'is_external',
    type: 'boolean',
    gp: 'اتریبیوت پیوند (rel)',
    gptooltip: REL_GROUP_TOOLTIP,
    title: 'external',
    tooltip: REL_EXTERNAL_TOOLTIP,
    width: 88,
    minWidth: 88,
    suppressHeaderMenuButton: true,
    cellRenderer: params => <BooleanRenderer boolValue={params.value} />
  },

  {
    searchable: false,
    field: 'is_ugc',
    type: 'boolean',
    gp: 'اتریبیوت پیوند (rel)',
    gptooltip: REL_GROUP_TOOLTIP,
    title: 'ugc',
    tooltip: REL_UGC_TOOLTIP,
    width: 75,
    minWidth: 75,
    suppressHeaderMenuButton: true,
    cellRenderer: params => <BooleanRenderer boolValue={params.value} />
  },
  {
    searchable: false,
    field: 'is_alternate',
    type: 'boolean',
    gp: 'اتریبیوت پیوند (rel)',
    gptooltip: REL_GROUP_TOOLTIP,
    title: 'alternate',
    tooltip: REL_ALTERNATE_TOOLTIP,
    width: 105,
    minWidth: 105,
    suppressHeaderMenuButton: true,
    cellRenderer: params => <BooleanRenderer boolValue={params.value} />
  },
  {
    searchable: false,
    field: 'is_noopener',
    type: 'boolean',
    gp: 'اتریبیوت پیوند (rel)',
    gptooltip: REL_GROUP_TOOLTIP,
    title: 'noopener',
    tooltip: REL_NOOPENER_TOOLTIP,
    width: 105,
    minWidth: 105,
    suppressHeaderMenuButton: true,
    cellRenderer: params => <BooleanRenderer boolValue={params.value} />
  },
  {
    searchable: false,
    field: 'is_noreferrer',
    type: 'boolean',
    gp: 'اتریبیوت پیوند (rel)',
    gptooltip: REL_GROUP_TOOLTIP,
    title: 'noreferrer',
    tooltip: REL_NOREFERRER_TOOLTIP,
    width: 115,
    minWidth: 115,
    suppressHeaderMenuButton: true,
    cellRenderer: params => <BooleanRenderer boolValue={params.value} />
  },
  {
    searchable: false,
    field: 'item_type',
    type: 'string',
    title: 'نوع آیتم',
    width: 85,
    minWidth: 85,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'page_last_seen',
    type: 'date',
    title: 'آخرین بررسی'
  }
];
// ANCHOR TEXT
export const AnchorTextcolumns = [
  {
    searchable: true,
    field: 'anchor',
    type: 'string',
    title: 'متن پیوند',
    flex: 2,
    minWidth: 200,
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      whiteSpace: 'normal',
      lineHeight: 1.35,
      wordBreak: 'break-word'
    },
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? safeDecode(params.value) : '[بدون متن پیوند]'
  },
  {
    searchable: true,
    field: 'backlink_count',
    type: 'number',
    title: 'تعداد بک‌لینک',
    flex: 1,
    minWidth: 90,
    cellRenderer: params => (
      <DrilldownToReferralPageCell
        displayValue={params.value}
        filterKey="Anchor"
        filterMode={params.data?.anchor ? 'equals' : 'blank'}
        filterValue={params.data?.anchor}
      />
    )
  },
  {
    searchable: true,
    field: 'distinct_host_from_count',
    type: 'number',
    title: 'تعداد دامنه',
    flex: 1,
    minWidth: 90
  },
  {
    searchable: true,
    field: 'follow_count',
    type: 'number',
    sortable: false,
    title: 'پیوند فالو',
    flex: 1,
    minWidth: 145,
    maxWidth: 280,
    cellRenderer: params => {
      return (
        <div className="d-flex gap-1 align-items-center">
          <span>{params.data.follow_count}</span>
          <span>{params.data.percentage_of_follow}%</span>
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <ProgressBar
              now={params.data.percentage_of_follow}
              style={{ height: 5 }}
              className="rounded-pill align-middle"
              variant="warning"
            />
          </span>
        </div>
      );
    }
  }
];

// REFERRING IP — aggregate by IPv4 /24 or /16 (see ip_prefix_length on API)
export const buildReferringIpColumns = (ipPrefixLength = 24) => [
  {
    searchable: true,
    field: 'subnet',
    type: 'string',
    title: ipPrefixLength === 16 ? 'Subnet /16' : 'Subnet /24',
    flex: 1,
    minWidth: 110,
    cellStyle: {
      direction: 'ltr',
      textAlign: 'left'
    }
  },
  {
    searchable: true,
    field: 'distinct_host_from_count',
    type: 'number',
    title: 'تعداد دامنه',
    flex: 1,
    minWidth: 80,
    sort: 'desc'
  },
  {
    searchable: true,
    field: 'backlink_count',
    type: 'number',
    title: 'تعداد بک‌لینک',
    flex: 1,
    minWidth: 90,
    cellRenderer: params => (
      <DrilldownToReferralPageCell
        displayValue={params.value}
        filterKey="DomainFromIp"
        filterMode="startswith"
        filterValue={referringIpDrilldownPrefix(params.data?.subnet)}
      />
    )
  },
  {
    searchable: true,
    field: 'follow_count',
    type: 'number',
    sortable: false,
    title: 'پیوند فالو',
    flex: 1,
    minWidth: 145,
    maxWidth: 280,
    cellRenderer: params => {
      return (
        <div className="d-flex gap-1 align-items-center">
          <span>{params.data.follow_count}</span>
          <span>{params.data.percentage_of_follow}%</span>
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <ProgressBar
              now={params.data.percentage_of_follow}
              style={{ height: 5 }}
              className="rounded-pill align-middle"
              variant="warning"
            />
          </span>
        </div>
      );
    }
  },
  {
    searchable: false,
    field: 'avg_moz_domain_authority',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'DA',
    tooltip: MOZ_DA_TOOLTIP,
    width: 67,
    minWidth: 67,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'avg_domain_from_rating_ahrefs',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'DR',
    tooltip: AHREFS_DR_TOOLTIP,
    width: 67,
    minWidth: 67,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'max_moz_spam_score',
    type: 'number',
    gp: 'معیار MOZ و Ahrefs',
    gptooltip: MOZ_GROUP_TOOLTIP,
    title: 'SS',
    tooltip: `${MOZ_SS_TOOLTIP}\n${MOZ_SPAM_SCORE_TOOLTIP}`,
    width: 74,
    minWidth: 74,
    suppressHeaderMenuButton: true
  },
  {
    searchable: false,
    field: 'high_spam_domain_count',
    type: 'number',
    title: 'دامنه پراسپم',
    width: 110,
    minWidth: 110
  },
  {
    searchable: false,
    field: 'top_domain_from_country',
    type: 'string',
    title: 'کشور غالب',
    flex: 1,
    minWidth: 90,
    cellRenderer: params => (
      <ReferralCountryCell
        country={params.value}
        countryCode={params.value}
      />
    )
  },
  {
    searchable: false,
    noFilter: true,
    field: 'link_last_seen',
    type: 'date',
    title: 'آخرین فعالیت',
    flex: 1,
    minWidth: 100
  },
  {
    searchable: false,
    noFilter: true,
    field: 'avg_moz_spam_score',
    type: 'number',
    title: 'میانگین SS',
    tooltip: MOZ_SPAM_SCORE_TOOLTIP,
    hide: true
  },
  {
    searchable: false,
    noFilter: true,
    field: 'max_moz_domain_authority',
    type: 'number',
    title: 'حداکثر DA',
    tooltip: MOZ_DA_TOOLTIP,
    hide: true
  },
  {
    searchable: false,
    noFilter: true,
    field: 'lost_backlink_count',
    type: 'number',
    title: 'بک‌لینک گم‌شده',
    hide: true
  },
  {
    searchable: false,
    noFilter: true,
    field: 'new_backlink_count',
    type: 'number',
    title: 'بک‌لینک جدید',
    hide: true
  }
];

export const ReferringIpSmallTable = [
  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'subnet',
    type: 'string',
    title: 'Subnet /24',
    flex: 1,
    minWidth: 0,
    cellStyle: {
      direction: 'ltr',
      textAlign: 'left'
    }
  },
  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'backlink_count',
    type: 'number',
    title: 'تعداد بک‌لینک',
    flex: 1,
    minWidth: 0,
    cellRenderer: params => (
      <DrilldownToReferralPageCell
        displayValue={params.value}
        filterKey="DomainFromIp"
        filterMode="startswith"
        filterValue={referringIpDrilldownPrefix(params.data?.subnet)}
      />
    )
  },
  {
    searchable: false,
    suppressHeaderMenuButton: true,
    field: 'distinct_host_from_count',
    type: 'number',
    title: 'تعداد دامنه',
    sort: 'desc',
    flex: 1,
    minWidth: 0
  }
];

/** Theme name for `badge-subtle-*`; same classes as the status chart on this page. */
const httpStatusBadgeBg = (statusCode: unknown): string => {
  const code = Number(statusCode);
  if (!Number.isFinite(code) || code <= 0) return 'secondary';
  if (code >= 200 && code < 300) return 'success';
  if (code >= 300 && code < 500) return 'warning';
  if (code >= 500) return 'danger';
  return 'secondary';
};

// INTERNAL PAGE
export const InternalPageColumns = [
  {
    searchable: true,
    field: 'url_to',
    type: 'string',
    wrapText: true,
    autoHeight: true,
    minWidth: 320,
    flex: 2,
    title: 'نشانی پیوند (href)',
    tooltip: LINK_HREF_TOOLTIP,
    cellStyle: {
      whiteSpace: 'normal',
      lineHeight: 1.35
    },
    cellRenderer: params => {
      const data = params.data;
      return (
        <BacklinkUrlCell
          url={params.value}
          breakAll
          isRedirected={isRedirectedUrl(
            data?.redirect_target,
            data?.is_indirect_link
          )}
          redirectTarget={data?.redirect_target}
          redirectStatusCode={data?.redirect_status_code}
          indirectLinkPath={data?.indirect_link_path}
        />
      );
    }
  },
  {
    searchable: false,
    field: 'url_to_status_code',
    type: 'number',
    title: 'وضعیت',
    tooltip: 'کد وضعیت HTTP نشانی مقصد پیوند.',
    width: 105,
    minWidth: 105,
    suppressHeaderMenuButton: true,
    cellRenderer: params => {
      const code = Number(params.value);
      const hasCode = Number.isFinite(code) && code > 0;
      return (
        <SubtleBadge bg={httpStatusBadgeBg(params.value)} className="fs--2">
          {hasCode ? code : '—'}
        </SubtleBadge>
      );
    }
  },
  {
    searchable: true,
    field: 'backlink_count',
    type: 'number',
    title: 'تعداد بک‌لینک',
    width: 140,
    minWidth: 130,
    maxWidth: 160,
    cellRenderer: params => (
      <DrilldownToReferralPageCell
        displayValue={params.value}
        filterKey="UrlTo"
        filterMode="equals"
        filterValue={params.data?.url_to}
      />
    )
  },
  {
    searchable: true,
    width: 170,
    minWidth: 160,
    maxWidth: 190,
    field: 'distinct_host_from_count',
    type: 'number',
    title: 'تعداد نشانی های دامنه'
  },
  {
    searchable: true,
    field: 'follow_count',
    type: 'number',
    sortable: false,
    title: 'پیوند فالو',
    flex: 1,
    minWidth: 180,
    maxWidth: 280,
    cellRenderer: params => {
      return (
        <div className="d-flex gap-1 align-items-center">
          <span>{params.data.follow_count}</span>
          <span>{params.data.percentage_of_follow}%</span>
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <ProgressBar
              now={params.data.percentage_of_follow}
              style={{ height: 5 }}
              className="rounded-pill align-middle"
              variant="warning"
            />
          </span>
        </div>
      );
    }
  },
  {
    searchable: true,
    field: 'nofollow_count',
    type: 'number',
    title: 'پیوند نوفالو',
    flex: 1,
    minWidth: 180,
    maxWidth: 280,
    cellRenderer: params => {
      return (
        <div className="d-flex gap-1 align-items-center">
          <span>{params.data.nofollow_count}</span>
          <span>{params.data.percentage_of_no_follow}%</span>
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <ProgressBar
              now={params.data.percentage_of_no_follow}
              style={{ height: 5 }}
              className="rounded-pill align-middle"
              variant="warning"
            />
          </span>
        </div>
      );
    }
  }
];
// TEXT TABLE
export const TableColumns = [
  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'name',
    title: 'دامنه ارجاع',
    flex: 1,
    minWidth: 0,
    cellRenderer: params => <URLRenderer url={params.value} />
  },
  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'value',
    sort: 'desc',
    title: 'تعداد',
    flex: 1,
    minWidth: 0
  },

  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'percentage',
    type: 'number',
    title: 'درصد از کل',
    flex: 1,
    minWidth: 0,
    cellRenderer: params => {
      return (
        <div className="d-flex gap-3 align-items-center">
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <ProgressBar
              now={params.value}
              style={{ height: 5 }}
              className="w-100 rounded-pill align-middle"
              variant="progress-gradient"
            />
          </span>
          <span>{params.value}%</span>
        </div>
      );
    }
  }
];

export const InternalPageSmallTable = [
  { ...TableColumns[0], title: 'صفحات داخلی' },
  ...TableColumns.slice(1)
];

export const AnchorTextSmallTable = [
  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'name',
    title: 'متن پیوند (anchor text)',
    flex: 1,
    minWidth: 0,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? safeDecode(params.value) : '[بدون متن پیوند]'
  },
  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'value',
    sort: 'desc',
    title: 'تعداد',
    flex: 1,
    minWidth: 0
  },

  {
    searchable: true,
    suppressHeaderMenuButton: true,
    field: 'percentage',
    type: 'number',
    title: 'درصد از کل',
    flex: 1,
    minWidth: 0,
    cellRenderer: params => {
      return (
        <div className="d-flex gap-3 align-items-center">
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            <ProgressBar
              now={params.value}
              style={{ height: 5 }}
              className="w-100 rounded-pill align-middle"
              variant="progress-gradient"
            />
          </span>
          <span>{params.value}%</span>
        </div>
      );
    }
  }
];

// SPAM DOMAINS
export const SpamDomainsColumns = [
  {
    searchable: true,
    field: 'host_name_from',
    type: 'string',
    title: 'نشانی دامنه',
    flex: 2,
    minWidth: 220,
    wrapText: true,
    autoHeight: true,
    cellRenderer: params => {
      const domain = String(params.value ?? '').trim();
      if (!domain) return null;
      const homepage = /^https?:\/\//i.test(domain)
        ? domain
        : `https://${domain}`;
      const spamLabel = getSpamDomainLabel(params.data?.moz_spam_score);

      return (
        <div
          className="d-flex flex-column align-items-start gap-1"
          dir="ltr"
          style={{ minWidth: 0, paddingBottom: '6px' }}
        >
          <a
            href={homepage}
            target="_blank"
            rel="noreferrer noopener"
            className="referral-domain-cell-link d-inline-flex align-items-center gap-1"
            style={{ maxWidth: '100%', overflow: 'hidden' }}
            title={`بازکردن ${homepage}`}
          >
            <img
              loading="lazy"
              width="16"
              height="16"
              className="rounded-circle"
              src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(domain)}`}
              alt=""
              onError={event => {
                event.currentTarget.style.display = 'none';
              }}
            />
            <span className="text-truncate">{domain}</span>
          </a>
          {spamLabel && (
            <SubtleBadge
              bg={spamLabel.color}
              className="fs--2 flex-shrink-0"
              style={{
                whiteSpace: 'nowrap',
                transform: 'translateY(-2px)',
                ...(spamLabel.color === 'secondary'
                  ? {
                      height: 'auto',
                      minHeight: 0,
                      lineHeight: 1.2,
                      fontSize: '0.7rem',
                      padding: '0.2rem 0.45rem',
                      backgroundColor: getColor('gray-300'),
                      color: getColor('gray-1000'),
                      border: `1px solid ${getColor('gray-500')}`
                    }
                  : {})
              }}
            >
              <span dir="rtl">{spamLabel.label}</span>
            </SubtleBadge>
          )}
        </div>
      );
    }
  },
  {
    searchable: true,
    field: 'moz_spam_score',
    type: 'number',
    title: 'دامنه اسپم',
    tooltip: MOZ_SPAM_SCORE_TOOLTIP,
    flex: 1,
    minWidth: 90
  },
  {
    searchable: false,
    field: 'moz_domain_authority',
    type: 'number',
    title: 'دامین اتوریتی',
    flex: 1,
    minWidth: 90
  },
  {
    searchable: false,
    field: 'backlink_count',
    type: 'number',
    title: 'تعداد بک‌لینک',
    flex: 1,
    minWidth: 110,
    cellRenderer: params => (
      <DrilldownToReferralPageCell
        displayValue={params.value}
        filterKey="HostNameFrom"
        filterMode="contains"
        filterValue={params.data?.host_name_from}
      />
    )
  },
  {
    searchable: true,
    field: 'domain_from_country',
    type: 'string',
    title: 'مکان دامنه',
    flex: 1,
    minWidth: 100,
    cellRenderer: params => (
      <ReferralCountryCell
        country={params.value}
        countryCode={params.data?.domain_from_country_code}
      />
    )
  },
  {
    searchable: true,
    field: 'tld_from',
    type: 'string',
    title: 'پسوند دامنه',
    flex: 1,
    minWidth: 80
  },
  {
    searchable: true,
    field: 'page_from_language',
    type: 'string',
    title: 'زبان دامنه',
    flex: 1,
    minWidth: 80
  },
  {
    searchable: false,
    noFilter: true,
    field: 'link_last_seen',
    type: 'date',
    title: 'آخرین بازدید',
    flex: 1,
    minWidth: 100
  }
];
